import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useCallback } from 'react';
import type { FormatNumberOptions } from 'react-intl';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { arrayToObject } from '@sorare/core/src/lib/arrays';
import {
  so5StatToBattingStatKey,
  so5StatToPitchingStatKey,
} from '@sorare/marketplace/src/shared/stats/PlayerGameScoreStats/utils';
import {
  StatsTableCell as Cell,
  StatsTableEmptyCell,
} from '@sorare/gameplay/src/atoms/StatsTable';

import { messages } from '#src/components/game/GameBoxScore/GameBoxPlayersTable/message.ts';

import type { GameStatsCells_PlayerGameScoreInterface } from './__generated__/index.graphql.ts';

const ThreeDigitCell = styled(Cell)`
  color: var(--c-nd-200);

  &.isPositive {
    color: var(--c-onBase-success);
  }
  &.isNegative {
    color: var(--c-red-300);
  }

  button {
    margin: auto;
  }
`;

type StatCellProps = {
  statKey: string;
  fantasyScore?: boolean;
  detailedStats?: Record<string, unknown>;
};

const StatCell = ({ detailedStats, statKey, fantasyScore }: StatCellProps) => {
  const { formatNumber } = useIntlContext();
  const formatNumber1Digit = useCallback(
    (value: number, options: FormatNumberOptions = {}) =>
      formatNumber(value, { maximumFractionDigits: 1, ...options }),
    [formatNumber]
  );

  if (!detailedStats?.[statKey]) {
    return <StatsTableEmptyCell />;
  }
  // Using `+ 0` to prevent '-0' from being displayed
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const score = detailedStats[statKey]?.totalScore + 0;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const stat = detailedStats[statKey]?.statValue;
  const tooltip = stat
    ? {
        ...messages.actualGameStat,
        values: { gameStat: formatNumber1Digit(stat) },
      }
    : undefined;

  const fantasyTooltip = score
    ? {
        ...messages.fantasyGameScore,
        values: { gameStat: formatNumber1Digit(score) },
      }
    : undefined;

  // Innings Pitched stat is displayed in a very special format
  if (!fantasyScore && statKey === 'pitching_innings_pitched') {
    const rawStatOrZero = stat || 0;
    const outs = Math.floor(rawStatOrZero / 3);
    const remaining = rawStatOrZero % 3;
    const remainingLabel = remaining > 0 ? `.${remaining}` : ``;
    const outStat = formatNumber(parseFloat(`${outs}${remainingLabel}`));
    return (
      <ThreeDigitCell
        title={fantasyScore ? tooltip : fantasyTooltip}
        className={classNames({ isNegative: score < 0, isPositive: score > 0 })}
      >
        {outStat}
      </ThreeDigitCell>
    );
  }
  return (
    <ThreeDigitCell
      title={fantasyScore ? tooltip : fantasyTooltip}
      className={classNames({ isNegative: score < 0, isPositive: score > 0 })}
    >
      {formatNumber1Digit(fantasyScore ? score : stat)}
    </ThreeDigitCell>
  );
};

type GameStatsCellsProps = {
  fantasyScore?: boolean;
  position?: 'pitching' | 'batting';
  playerGameScore?: GameStatsCells_PlayerGameScoreInterface | null;
};

export const GameStatsCells = ({
  position,
  fantasyScore,
  playerGameScore,
}: GameStatsCellsProps) => {
  const COLUMN_DEFS =
    position === 'pitching'
      ? so5StatToPitchingStatKey
      : so5StatToBattingStatKey;

  const groupedByStat: Record<string, unknown> = arrayToObject({
    value: s => s,
    key: s => s.stat,
    list: playerGameScore?.detailedScore || [],
  });

  return (
    <>
      <Cell>
        <Horizontal center>
          {playerGameScore ? (
            <Score
              score={playerGameScore.score}
              scoreStatus={playerGameScore.scoreStatus}
              shape="PILL"
              scoreType="PLAYER_SCORE"
              hideReviewing
            />
          ) : (
            '-'
          )}
        </Horizontal>
      </Cell>
      {Object.keys(COLUMN_DEFS).map(key => (
        <StatCell
          key={key}
          statKey={key}
          fantasyScore={fantasyScore}
          detailedStats={groupedByStat}
        />
      ))}
    </>
  );
};

GameStatsCells.fragments = {
  PlayerGameScoreInterface: gql`
    fragment GameStatsCells_PlayerGameScoreInterface on PlayerGameScoreInterface {
      id
      score
      scoreStatus
      detailedScore {
        stat
        statValue
        totalScore
      }
    }
  ` as TypedDocumentNode<GameStatsCells_PlayerGameScoreInterface>,
};
